
import { UserProfile } from '@/types/auth';
import { supabase } from '@/integrations/supabase/client';

// Save user profile to localStorage
export const saveUserToStorage = (user: UserProfile): void => {
  try {
    const userJson = JSON.stringify(user);
    localStorage.setItem('user', userJson);
  } catch (e) {
    // Handle error silently
  }
};

// Get user profile from localStorage
export const getUserFromStorage = (): UserProfile | null => {
  try {
    const userJson = localStorage.getItem('user');
    if (!userJson) return null;
    return JSON.parse(userJson);
  } catch (e) {
    return null;
  }
};

// Remove user profile from localStorage
export const removeUserFromStorage = (): void => {
  try {
    localStorage.removeItem('user');
  } catch (e) {
    // Handle error silently
  }
};

// Fetch user profile from Supabase
export const fetchUserProfile = async (userId: string): Promise<any> => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();
      
    if (error) {
      return null;
    }
    
    return data;
  } catch (error) {
    return null;
  }
};

// Create user profile object from session
export const createUserProfile = async (session: any): Promise<UserProfile | null> => {
  if (!session?.user) return null;
  
  try {
    const profileData = await fetchUserProfile(session.user.id);
      
    const userProfile: UserProfile = {
      id: session.user.id,
      name: profileData?.name || session.user.user_metadata?.name || 'User',
      email: session.user.email || '',
      phone: profileData?.phone || session.user.user_metadata?.phone || '',
      age: profileData?.age || session.user.user_metadata?.age || 21,
      educationStatus: (profileData?.education_status as any) || 
                      session.user.user_metadata?.education_status || 
                      'completed12th',
    };
    
    return userProfile;
  } catch (error) {
    return null;
  }
};

// Update user profile in Supabase
export const updateUserProfile = async (
  user: UserProfile, 
  updates: Partial<UserProfile>
): Promise<UserProfile> => {
  if (!user?.id) throw new Error('User ID is required to update profile');
  
  try {
    // Update profile in database
    const { error } = await supabase
      .from('profiles')
      .update({
        name: updates.name,
        phone: updates.phone,
        education_status: updates.educationStatus,
        age: updates.age,
        updated_at: new Date().toISOString()
      })
      .eq('id', user.id);
    
    if (error) throw error;
    
    // Return updated user profile
    const updatedUser = { ...user, ...updates };
    saveUserToStorage(updatedUser);
    
    return updatedUser;
  } catch (error) {
    throw error;
  }
};
